import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  HLM_DIALOG_SHELL,
  HlmBtnDirective,
  HlmCheckboxComponent,
  HlmIconComponent,
} from '../../ui';

export interface LargeRangeConfirmData {
  /** Range size to display in the body, e.g. "67 days". */
  rangeText: string;
}

export interface LargeRangeConfirmResult {
  proceed: boolean;
  remember: boolean;
}

/**
 * Confirm modal shown when the user submits a search with a time range that exceeds
 * the recommended limit. Lets them opt out for the rest of the session via a checkbox.
 */
@Component({
  selector: 'cmt-large-range-confirm-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HLM_DIALOG_SHELL, HlmBtnDirective, HlmIconComponent, HlmCheckboxComponent],
  template: `
    <hlm-dialog-surface size="sm">
      <hlm-dialog-header>
        <hlm-dialog-icon-badge tone="warning">
          <hlm-icon name="lucideTriangleAlert" size="1.25rem" />
        </hlm-dialog-icon-badge>
        <div hlmDialogHeading>
          <h2 hlmDialogTitle>Large time range</h2>
          <p hlmDialogDescription>
            You're about to search a range spanning <strong>{{ data.rangeText }}</strong>.
            This query may be slow and return a large response.
          </p>
        </div>
        <button hlmDialogClose (click)="cancel()">
          <hlm-icon name="lucideX" size="1rem" />
        </button>
      </hlm-dialog-header>

      <section hlmDialogBody>
        <label class="flex items-center gap-2 text-sm text-foreground">
          <hlm-checkbox [checked]="rememberChoice()" (checkedChange)="rememberChoice.set($event)" />
          <span>Don't ask again for this session</span>
        </label>
      </section>

      <footer hlmDialogFooter>
        <button hlmBtn variant="outline" type="button" (click)="cancel()">Cancel</button>
        <button hlmBtn variant="default" type="button" (click)="confirm()">
          <hlm-icon name="lucideSearch" size="0.9rem" />
          Continue search
        </button>
      </footer>
    </hlm-dialog-surface>
  `,
})
export class LargeRangeConfirmDialogComponent {
  readonly data = inject<LargeRangeConfirmData>(MAT_DIALOG_DATA);
  private readonly ref = inject<MatDialogRef<LargeRangeConfirmDialogComponent, LargeRangeConfirmResult>>(MatDialogRef);

  readonly rememberChoice = signal(false);

  cancel(): void {
    this.ref.close({ proceed: false, remember: false });
  }
  confirm(): void {
    this.ref.close({ proceed: true, remember: this.rememberChoice() });
  }
}
